/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, SxProps, Grid } from "@mui/material";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DIALOGOPENFOR, IRouteInfo, GEO_REDUNDANCY_PATH } from "../../globals/globals";

import "./Navigation.css";
import { useStoreDispatch } from "../../store/hooks";
import { resetKeyword } from "../../store/search/reducer";
import { resetDeviceFilters } from "../../store/filters/Nodes/reducer";
import { setBasicDialogOpenFor } from "../../store/helpers/helperReducers";
import { useGeoRedundancy } from "../../pages/Administrations/GeoRedundancy/hooks";

const AdminNavigationList: React.FC<{ navMenuItems: IRouteInfo[]; onSelectIndex?: (index: number) => void }> = ({ navMenuItems, onSelectIndex }) => {
  const { data, isLoading, error } = useGeoRedundancy()
  const dispatch = useStoreDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const locationPath: string = location?.pathname.split("/").slice(-1).toString() || "";
  const activeIndex = navMenuItems.filter((item: IRouteInfo) => item?.Path === locationPath)[0]?.Key || navMenuItems[0]?.Key;

  const [selectedIndex, setSelectedIndex] = React.useState(activeIndex);

  const handleListItemClick = (index: number, navigatePath: string) => {
    setSelectedIndex(index);
    onSelectIndex?.(index);
    navigate(navigatePath);
    dispatch(resetKeyword());
    dispatch(resetDeviceFilters());
  };

  const handleAddSite = () => {
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ADDSITEPOPUP));
  }

  useEffect(() => {
    setSelectedIndex(activeIndex);
  }, [activeIndex, locationPath, selectedIndex]);

  const listItemButtonSxprops: SxProps = {
    width: 208,
    height: 32,
    pt: 0,
    pb: 0,
    "&.Mui-selected": {
      backgroundColor: "var(--sidebar-accent) !important",
      borderRadius: "5px",
    },
  };

  const filteredNavMenuItems = data?.enabled
    ? navMenuItems
    : navMenuItems.filter(item => item.Path !== GEO_REDUNDANCY_PATH);

  return (
    <List sx={{ width: "100%", minWidth: "10px", maxHeight: "auto", mt: 2, mb: 4, overflow: "auto" }}>
      {filteredNavMenuItems.map((item) => {
        return (
          <ListItem key={item.Key} disablePadding onClick={() => handleListItemClick(item.Key, item.Path)}>
            <ListItemButton style={{ margin: "3px", padding: "6px" }} selected={selectedIndex === item.Key} sx={listItemButtonSxprops}>
              {item?.Icon && (
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <item.Icon sx={{ color: 'var(--sidebar-foreground)', fontSize: 20 }} />
                </ListItemIcon>
              )}
              <ListItemText primary={item.Text}
                primaryTypographyProps={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
      <Grid xs={12} p={2}>
        <span
          className="block h-px w-full"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          aria-hidden
        />
      </Grid>
      <ListItem>
        <ListItemButton sx={listItemButtonSxprops} onClick={handleAddSite} >
          <ListItemText primary={"Add Device"}
            primaryTypographyProps={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          />
        </ListItemButton>
      </ListItem>
    </List>
  );
};

export default AdminNavigationList;